import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { io } from 'socket.io-client';
import type { RealtimeEvent } from '@rundown/types';
import { authClient } from '../../lib/auth';
import { API_BASE_URL } from '../../lib/env';
import { queryKeys } from '../../lib/queryKeys';
import { isRecord } from '../../lib/guards';

/**
 * The realtime feed. It carries no data into the UI — it only tells TanStack
 * Query what has gone stale.
 *
 * That is a deliberate constraint. If socket payloads were written straight into
 * the cache, the app would have two ways for a request to arrive (fetch and
 * push) that could disagree, and every event would need to be a complete,
 * correctly-shaped row. Invalidating instead means the server stays the single
 * source of truth and an event only has to be *right about what changed*.
 */

/** Server event channel. One channel, many `type`s inside the envelope. */
const EVENT_CHANNEL = 'rundown:event';

/**
 * ASSUMED CONTRACT: the envelope is exactly `RealtimeEvent` from @rundown/types
 * — id, workspaceId, type, audience, payload, createdAt. Nothing is added to it
 * here; the alias exists only so the parser below has one name to narrow to.
 */
type RundownRealtimeEvent = RealtimeEvent;

/** Events come off a socket: validate rather than trust the shape. */
function asRealtimeEvent(value: unknown): RundownRealtimeEvent | null {
  if (!isRecord(value)) return null;
  const { workspaceId, type } = value;
  if (typeof workspaceId !== 'string' || typeof type !== 'string') return null;
  return value as unknown as RundownRealtimeEvent;
}

/** Pulls an entity id out of the payload when the event names one. */
function payloadId(event: RundownRealtimeEvent, field: string): string | null {
  if (!isRecord(event.payload)) return null;
  const value = event.payload[field];
  return typeof value === 'string' ? value : null;
}

/**
 * Which caches an event invalidates.
 *
 * The fallback is the whole workspace subtree, and it is safe *only* because
 * every key starts ['ws', workspaceId] — prefix matching then bounds the blast
 * radius to one tenant. An unrecognised event over-fetching a little beats an
 * unrecognised event leaving a stale board on screen.
 */
function keysToInvalidate(event: RundownRealtimeEvent): readonly (readonly unknown[])[] {
  const ws = event.workspaceId;

  if (event.type.startsWith('request.')) {
    // The Review inbox is included for every request event, not only
    // `request.reviewed`: a request arriving, being confirmed, or having its
    // type changed all move it in or out of that queue, and the event does not
    // carry enough to tell which.
    const requestId = payloadId(event, 'id') ?? payloadId(event, 'requestId');
    // A field edit can move sprint, owner or estimate, all of which the
    // planning surfaces (backlog, its projection, the delivery read) show.
    // The workspace activity feed too — it is a live record of exactly these
    // events, and a deletion or a merge that does not appear on the audit trail
    // until a reload is a record people would learn not to trust.
    // Threads too: a request event can be a chat turning into an issue, an
    // issue settling (title, badge, status), or one going back to a chat.
    return requestId === null
      ? [
          queryKeys.requests(ws),
          queryKeys.reviewQueue(ws),
          queryKeys.backlog(ws),
          queryKeys.activity(ws),
          ['ws', ws, 'delivery'],
          queryKeys.threads(ws),
        ]
      : [
          queryKeys.threads(ws),
          ['ws', ws, 'intake', requestId],
          queryKeys.requests(ws),
          queryKeys.reviewQueue(ws),
          queryKeys.backlog(ws),
          queryKeys.activity(ws),
          ['ws', ws, 'delivery'],
          queryKeys.request(ws, requestId),
          // request.attachment_added lands here too, and the key is cheap to
          // refetch. A file appearing on someone else's screen a second after
          // it was dropped is the behaviour this whole channel exists for.
          queryKeys.requestAttachments(ws, requestId),
        ];
  }

  if (event.type.startsWith('message.')) {
    const requestId = payloadId(event, 'requestId');
    // The thread list moves on every message: order, snippet, unread dot.
    return requestId === null
      ? [queryKeys.workspace(ws)]
      : [
          queryKeys.requestMessages(ws, requestId),
          queryKeys.requestAttachments(ws, requestId),
          queryKeys.threads(ws),
        ];
  }

  /**
   * Home's thread list. `thread.read` comes only to the reader's own sockets
   * (their other tabs) and moves nothing but unread dots; the rest change
   * what a thread IS — created, converted to an issue, kept as a chat — so the
   * request and its intake state go too.
   */
  if (event.type.startsWith('thread.')) {
    const requestId = payloadId(event, 'requestId');
    if (event.type === 'thread.read' || requestId === null) return [queryKeys.threads(ws)];
    return [
      queryKeys.threads(ws),
      queryKeys.request(ws, requestId),
      queryKeys.requestMessages(ws, requestId),
      ['ws', ws, 'intake', requestId],
      queryKeys.reviewQueue(ws),
    ];
  }

  // A sprint or board move changes where requests sit, so the list goes too.
  // A sprint write (start, release, delay, a change of plan) moves where work
  // sits on every planning surface at once — the delivery read model, the
  // backlog and its projection, the board's lanes and the plan-change list.
  if (event.type.startsWith('sprint.') || event.type.startsWith('backlog.')) {
    return [
      queryKeys.sprints(ws),
      queryKeys.requests(ws),
      queryKeys.backlog(ws),
      queryKeys.board(ws),
      queryKeys.activity(ws),
      ['ws', ws, 'delivery'],
      ['ws', ws, 'plan-changes'],
    ];
  }
  if (event.type.startsWith('board.'))
    return [queryKeys.board(ws), queryKeys.requests(ws), ['ws', ws, 'delivery']];

  /**
   * An hour logged moves three things and nothing else: the entries on that
   * request, the card's running total on the Board, and the trail.
   *
   * Before this branch existed, `time_entry.logged` fell through to the
   * catch-all below and invalidated the ENTIRE workspace subtree on every
   * connected client — every list, every screen, every open request — every
   * time anyone anywhere logged time. On a day when the team is logging
   * against a standup that is a refetch storm arriving while people are trying
   * to use the app, and it reads exactly like the app being slow rather than
   * like the network being busy.
   *
   * The catch-all is still the right default for an event nobody has taught
   * this function about. It is the wrong answer for the most frequent event
   * the system emits.
   */
  if (event.type.startsWith('time_entry.')) {
    const requestId = payloadId(event, 'requestId');
    return requestId === null
      ? [queryKeys.board(ws)]
      : [
          ['ws', ws, 'requests', requestId, 'time-entries'],
          queryKeys.board(ws),
          queryKeys.requestActivity(ws, requestId),
        ];
  }

  /**
   * Settings events: administration, and — in one case — something every
   * working screen IS showing.
   *
   * The profile is in this list because the workspace it carries holds the size
   * thresholds, and every card that prints "Medium" derives that label from
   * them locally. Without this line, moving a band in Settings re-labels
   * requests for the person who moved it and for nobody else, until each of
   * them happens to reload. `queryKeys.profile()` is outside the ['ws', …]
   * namespace on purpose (see lib/queryKeys.ts), so a prefix invalidation
   * cannot reach it and it has to be named.
   */
  if (
    event.type.startsWith('settings.') ||
    event.type.startsWith('secret.') ||
    event.type.startsWith('membership.')
  ) {
    return [['ws', ws, 'settings'], queryKeys.workspace(ws), queryKeys.profile()];
  }

  if (event.type.startsWith('project.')) {
    return [queryKeys.settingsProjects(ws), queryKeys.requests(ws), ['ws', ws, 'projects']];
  }

  return [queryKeys.workspace(ws)];
}

/** Connects for as long as the component is mounted. Mount it once, inside <RequireAuth>. */
export function useRealtime(): void {
  const queryClient = useQueryClient();

  useEffect(() => {
    // The ORIGIN, not API_BASE_URL: socket.io reads a URL's path as the
    // namespace, so production's `/api` would target a namespace the gateway
    // does not have. The transport lives at /socket.io on the origin.
    const socket = io(new URL(API_BASE_URL).origin, {
      // A function, so every reconnect sends the SDK's current token rather than
      // the one captured when this effect first ran.
      auth: (send) => {
        void authClient.getAccessToken().then((token) => send({ token }));
      },
    });

    // NOTE: no `query: { workspaceId }`, and no emit('join', …) anywhere in this
    // file. The server puts this socket into rooms based on the session it just
    // authenticated. A workspace id sent from the client would be a subscription
    // request the client is not entitled to make — and the one place where a
    // tampered browser could listen to another tenant's traffic.

    let hasConnected = false;

    socket.on('connect', () => {
      if (hasConnected) {
        // A reconnect means there is a hole: events that fired while the socket
        // was down were delivered to nobody. Everything on screen is suspect.
        void queryClient.invalidateQueries();
      }
      hasConnected = true;
    });

    socket.on(EVENT_CHANNEL, (raw: unknown) => {
      const event = asRealtimeEvent(raw);
      if (event === null) return;

      // A tab refetches on the echo of its own write, and that is accepted
      // rather than optimised away.
      //
      // There used to be a guard here comparing the event's `originClientId` to
      // this tab's id, and it had never once matched: `app.events` has no origin
      // column and `OutboxService` was never given one, so the field was always
      // undefined. It is gone rather than finished. Closing it properly means a
      // migration plus threading a header through WorkspaceContext into all 21
      // `outbox.emit` call sites, and what it buys is one redundant refetch per
      // write, in one tab. The flicker it was written to prevent is already
      // handled on the surfaces that would show it — lib/optimistic.ts
      // reconciles with `setQueryData` instead of invalidating, which is the
      // half of that problem that actually needed solving.

      for (const queryKey of keysToInvalidate(event)) {
        void queryClient.invalidateQueries({ queryKey });
      }
    });

    return () => {
      // removeAllListeners before disconnect: a queued event must not land on a
      // handler that closes over a QueryClient this effect is done with.
      socket.removeAllListeners();
      socket.disconnect();
    };
  }, [queryClient]);
}
